import type { GlossaryTerm } from "./glossary";

/**
 * Термины аналитики рекламной кампании: русское название (аббревиатура — в скобках), смысл, формула и пример.
 * Примеры — на одной условной кампании: бюджет 10 000 ₽, пришли 100 человек, активировались 40,
 * со сделкой 30, доход площадки 12 000 ₽, затраты на промо 1 000 ₽, оборот 200 000 ₽.
 */
export const CAMPAIGN_GLOSSARY = {
    // Обзор
    campRegistered: {
        label: "Пришли",
        expand: "сколько человек привела кампания",
        meaning:
            "Все, кто впервые пришёл на площадку по ссылке кампании или с её промокодом. Человек засчитывается только первой кампании, по которой пришёл.",
        formula: "число людей, закреплённых за кампанией",
        example: "По ссылке зарегистрировались 80 человек, ещё 20 ввели промокод — пришли 100.",
    },
    campActivated: {
        label: "Активировались",
        expand: "сделали первое целевое действие",
        meaning: "Пришедшие, которые после прихода пополнили баланс, купили или продали скин.",
        formula: "пришедшие, у которых после прихода есть депозит, покупка или продажа",
        example: "Из 100 пришедших 25 пополнили баланс, ещё 15 сразу купили с бонусного баланса — активировались 40.",
    },
    campTraded: {
        label: "Со сделкой",
        expand: "купили или продали",
        meaning:
            "Пришедшие, у которых после прихода есть завершённая покупка или продажа. Возврат считается продажей для продавца, но не покупкой для покупателя.",
        formula: "пришедшие с покупкой или продажей после прихода",
        example: "20 человек купили, 15 продали, 5 из них сделали и то и другое — со сделкой 30.",
    },
    campActivationRate: {
        label: "Доля активации",
        expand: "какая часть пришедших активировалась",
        meaning: "Показывает, сколько пришедших дошли до первого целевого действия.",
        formula: "активировались ÷ пришли",
        example: "40 ÷ 100 = 40%.",
    },
    campContribution: {
        label: "Доход площадки",
        expand: "сколько площадка заработала на пришедших",
        meaning:
            "Комиссии с покупок пришедших плюс наш доход с их пополнений и выводов. Скидки по промокодам сюда не входят — они в «Затратах на промо».",
        formula: "комиссии с покупок + доход с пополнений + доход с выводов",
        example: "Комиссии 9 000 ₽ + с пополнений 2 000 ₽ + с выводов 1 000 ₽ = 12 000 ₽.",
    },
    campPromoCost: {
        label: "Затраты на промо",
        expand: "во сколько обошлись бонусы по промокодам",
        meaning:
            "Скидки покупателям, недобор комиссии с продавцов и бонусы к пополнениям у пришедших — только после их прихода и в сроки кампании.",
        formula: "сумма бонусов, которые получили пришедшие",
        example: "Скидка 1% с покупки за 100 ₽ — это 1 ₽ затрат. 1 000 таких покупок — 1 000 ₽.",
    },
    campNetResult: {
        label: "Чистый результат",
        expand: "заработали минус потратили",
        meaning: "Доход площадки после вычета бюджета кампании и затрат на промо. «—», если бюджет не указан.",
        formula: "доход площадки − бюджет − затраты на промо",
        example: "12 000 − 10 000 − 1 000 = 1 000 ₽ — кампания в плюсе.",
    },
    campArpu: {
        label: "Доход на пришедшего (ARPU)",
        expand: "средний доход с одного пришедшего",
        meaning: "Сколько в среднем площадка заработала на каждом пришедшем, включая тех, кто ничего не сделал.",
        formula: "доход площадки ÷ пришли",
        example: "12 000 ₽ ÷ 100 = 120 ₽.",
    },
    campArppu: {
        label: "Доход на платящего (ARPPU)",
        expand: "средний доход с того, кто принёс доход",
        meaning: "Как «Доход на пришедшего», но делим только на тех, кто принёс площадке ненулевой доход.",
        formula: "доход площадки ÷ пришедшие с ненулевым доходом",
        example: "Ненулевой доход у 27 человек (25 в плюсе и 2 в минусе): 12 000 ₽ ÷ 27 ≈ 444 ₽.",
    },
    campLtvActivated: {
        label: "Доход на активировавшегося (LTV)",
        expand: "средний доход с активировавшегося",
        meaning: "Доход, который принесли активировавшиеся, делённый на их число. Считается за срок кампании.",
        formula: "доход от активировавшихся ÷ активировались",
        example: "Активировавшиеся принесли 11 000 ₽: 11 000 ÷ 40 = 275 ₽.",
    },
    campSpend: {
        label: "Бюджет",
        expand: "сколько потратили на рекламу",
        meaning: "Сумма, указанная в настройках кампании. Считается целиком, без разбивки по дням.",
        formula: "бюджет из настроек кампании",
        example: "Заплатили блогеру 10 000 ₽ — бюджет 10 000 ₽.",
    },
    campCac: {
        label: "Цена привлечения (CAC)",
        expand: "сколько стоил один пришедший",
        meaning: "Бюджет кампании в расчёте на одного пришедшего.",
        formula: "бюджет ÷ пришли",
        example: "10 000 ₽ ÷ 100 = 100 ₽ за человека.",
    },
    campCostPerActivation: {
        label: "Цена активации",
        expand: "сколько стоил один активировавшийся",
        meaning: "Бюджет кампании в расчёте на одного активировавшегося.",
        formula: "бюджет ÷ активировались",
        example: "10 000 ₽ ÷ 40 = 250 ₽.",
    },
    campCostPerTrade: {
        label: "Цена человека со сделкой",
        expand: "сколько стоил один человек со сделкой (не одна сделка)",
        meaning: "Бюджет кампании в расчёте на одного пришедшего, который купил или продал.",
        formula: "бюджет ÷ со сделкой",
        example: "10 000 ₽ ÷ 30 ≈ 333 ₽.",
    },
    campRoas: {
        label: "Окупаемость бюджета (ROAS)",
        expand: "сколько вернулось на каждый рубль бюджета",
        meaning: "Доход площадки за вычетом затрат на промо, делённый на бюджет. Больше 1 — бюджет отбился.",
        formula: "(доход площадки − затраты на промо) ÷ бюджет",
        example: "(12 000 − 1 000) ÷ 10 000 = 1.10× — на рубль бюджета вернули 1.10 ₽.",
    },
    campRomi: {
        label: "Рентабельность (ROMI)",
        expand: "сколько заработали сверх бюджета",
        meaning: "Чистый результат, делённый на бюджет. Больше 0 — кампания в плюсе, меньше 0 — в минусе.",
        formula: "(доход площадки − затраты на промо − бюджет) ÷ бюджет",
        example: "(12 000 − 1 000 − 10 000) ÷ 10 000 = 0.10, то есть +10% к бюджету.",
    },
    campLtvCac: {
        label: "Доход к цене привлечения (LTV/CAC)",
        expand: "во сколько раз доход с человека больше его цены",
        meaning: "Доход на пришедшего, делённый на цену привлечения. Больше 1 — пришедшие окупают свою цену.",
        formula: "доход на пришедшего ÷ цена привлечения",
        example: "120 ₽ ÷ 100 ₽ = 1.20.",
    },
    campSpendShare: {
        label: "Бюджет к доходу",
        expand: "какую часть дохода съела реклама",
        meaning: "Бюджет кампании относительно дохода площадки. Больше 100% — реклама стоила дороже, чем принесла.",
        formula: "бюджет ÷ доход площадки",
        example: "10 000 ÷ 12 000 ≈ 83%.",
    },
    campGmv: {
        label: "Оборот (GMV)",
        expand: "на какую сумму пришедшие купили и продали",
        meaning:
            "Сумма цен скинов в сделках, где пришедший — покупатель или продавец, включая возвраты. Если и покупатель, и продавец из пришедших, сделка считается один раз — поэтому оборот покупок плюс оборот продаж может быть больше.",
        formula: "сумма цен скинов в сделках пришедших после их прихода",
        example: "Купили у посторонних на 150 000 ₽, продали посторонним на 50 000 ₽ — оборот 200 000 ₽.",
    },
    campSpendShareOfGmv: {
        label: "Бюджет к обороту",
        expand: "какую часть оборота составила реклама",
        meaning: "Бюджет кампании относительно оборота пришедших.",
        formula: "бюджет ÷ оборот",
        example: "10 000 ÷ 200 000 = 5%.",
    },

    // Воронка
    campFunnel: {
        label: "Воронка",
        expand: "сколько пришедших дошло до каждого шага",
        meaning: "Пришли → пополнили баланс → купили. Каждый шаг считается от момента прихода до конца кампании.",
        formula: "доля шага = дошли до шага ÷ пришли",
        example: "Из 100 пришедших 25 пополнили (25%) и 20 купили (20%).",
    },
    campDeposited: {
        label: "С депозитом",
        expand: "пополнили баланс",
        meaning: "Пришедшие, которые после прихода хотя бы раз пополнили баланс. Под числом — доля от пришедших.",
        formula: "пополнившие ÷ пришли",
        example: "25 из 100 — 25%.",
    },
    campBought: {
        label: "С покупкой",
        expand: "купили скин",
        meaning: "Пришедшие с завершённой покупкой после прихода. Возврат покупкой не считается.",
        formula: "купившие ÷ пришли",
        example: "20 из 100 — 20%.",
    },
    campSold: {
        label: "С продажей",
        expand: "продали скин",
        meaning: "Пришедшие, которые после прихода продали скин (в том числе если покупателю потом сделали возврат).",
        formula: "продавшие ÷ пришли",
        example: "15 из 100 — 15%.",
    },
    campWithoutDeposit: {
        label: "Без депозита",
        expand: "так и не пополнили баланс",
        meaning: "Доля пришедших, которые ни разу не пополнили баланс.",
        formula: "(пришли − с депозитом) ÷ пришли",
        example: "(100 − 25) ÷ 100 = 75%.",
    },
    campWithoutPurchase: {
        label: "Без покупки",
        expand: "так и не купили",
        meaning: "Доля пришедших без единой завершённой покупки.",
        formula: "(пришли − с покупкой) ÷ пришли",
        example: "(100 − 20) ÷ 100 = 80%.",
    },
    campDepositToPurchase: {
        label: "Депозит → покупка",
        expand: "сколько пополнивших потом купили",
        meaning: "Доля купивших среди тех, кто пополнил баланс. Покупатели без пополнения сюда не входят. «—», если никто не пополнял.",
        formula: "пополнили и купили ÷ пополнили",
        example: "Пополнили 25, из них купили 15: 15 ÷ 25 = 60%.",
    },
    campTimeToDeposit: {
        label: "Время до депозита (TTFD)",
        expand: "сколько ждали первого пополнения",
        meaning: "Время от прихода по кампании до первого пополнения баланса — только у тех, кто пополнил.",
        formula: "дата первого пополнения − дата прихода",
        example: "Пришёл в понедельник в 10:00, пополнил во вторник в 12:00 — 26 часов.",
    },
    campTimeToPurchase: {
        label: "Время до покупки (TTFT)",
        expand: "сколько ждали первой покупки",
        meaning: "Время от прихода по кампании до первой завершённой покупки — только у тех, кто купил.",
        formula: "дата первой покупки − дата прихода",
        example: "Пришёл в 10:00, купил в 13:00 того же дня — 3 часа.",
    },
    campMedian: {
        label: "Медиана",
        expand: "время типичного человека",
        meaning:
            "Среди тех, кто сделал этот шаг, половина уложилась быстрее этого времени, половина — медленнее. Не сбивается редкими долгими случаями.",
        formula: "середина упорядоченного списка времён",
        example: "Времена 1, 2, 3, 10 и 100 часов — медиана 3 часа (а среднее было бы 23).",
    },
    campP90: {
        label: "90% уложились (P90)",
        expand: "время, за которое успели почти все",
        meaning:
            "Среди тех, кто сделал этот шаг, 9 из 10 уложились в это время, а 1 из 10 шёл дольше. Показывает «хвост» медленных.",
        formula: "время, быстрее которого успели 90% людей",
        example: "Из 10 человек 9 пополнили за сутки, десятый — через неделю: 90% уложились в 24 часа.",
    },

    // Качество
    campSingleTrade: {
        label: "Одна покупка",
        expand: "купили ровно один раз",
        meaning: "Доля пришедших ровно с одной покупкой — купили и не вернулись.",
        formula: "пришедшие ровно с одной покупкой ÷ пришли",
        example: "12 человек купили один раз: 12 ÷ 100 = 12%.",
    },
    campRepeatDeposit: {
        label: "Повторный депозит",
        expand: "пополнили второй раз",
        meaning: "Доля тех, кто пополнил баланс 2 и больше раз, среди всех пополнивших. Под числом — сколько из скольких.",
        formula: "пополнили 2+ раза ÷ пополнили хотя бы раз",
        example: "Пополнили 25, из них ещё раз — 10: 10 ÷ 25 = 40%.",
    },
    campRepeatPurchase: {
        label: "Повторная покупка",
        expand: "купили второй раз",
        meaning: "Доля пришедших с 2 и больше покупками. Под числом — та же доля только среди купивших.",
        formula: "купили 2+ раза ÷ пришли",
        example: "8 человек купили повторно: 8 ÷ 100 = 8%; среди 20 купивших — 40%.",
    },
    campPurchasesPerBuyer: {
        label: "Покупок на покупателя",
        expand: "сколько раз в среднем покупает купивший",
        meaning: "Среднее число покупок у тех, кто купил хотя бы раз.",
        formula: "число покупок ÷ купившие",
        example: "20 купивших сделали 36 покупок: 36 ÷ 20 = 1.80.",
    },
    campPositive: {
        label: "Приносят доход",
        expand: "площадка на них заработала",
        meaning: "Доля пришедших, чей доход площадки больше нуля.",
        formula: "пришедшие с доходом больше 0 ÷ пришли",
        example: "25 из 100 — 25%.",
    },
    campZero: {
        label: "Ноль",
        expand: "площадка на них ничего не заработала",
        meaning: "Доля пришедших с нулевым доходом: ничего не сделали или сделали без комиссии.",
        formula: "пришедшие с доходом 0 ÷ пришли",
        example: "Купил скин без комиссии и ничего не пополнял — доход 0. 73 из 100 — 73%.",
    },
    campNegative: {
        label: "В минусе",
        expand: "площадка на них потеряла",
        meaning: "Доля пришедших с отрицательным доходом: например, вывод обошёлся дороже, чем мы на нём заработали.",
        formula: "пришедшие с доходом меньше 0 ÷ пришли",
        example: "2 из 100 — 2%.",
    },
    campRetention: {
        label: "Удержание",
        expand: "возвращаются ли пришедшие",
        meaning:
            "Доля пришедших, которые заходили на площадку через 1, 7 и 30 дней после прихода. Считаем только тех, кто пришёл достаточно давно.",
        formula: "вернулись через N дней ÷ пришли не меньше N дней назад",
        example: "Неделю назад и раньше пришли 60 человек, через 7+ дней заходили 18: 18 ÷ 60 = 30%.",
    },
    campD1: {
        label: "Вернулись через день (D1)",
        expand: "заходили на следующий день и позже",
        meaning: "Доля пришедших, которые заходили на площадку через сутки после прихода или позже.",
        formula: "заходили через 1+ день ÷ пришли не меньше дня назад",
        example: "Из 90 пришедших вчера и раньше вернулись 45 — 50%.",
    },
    campD7: {
        label: "Вернулись через неделю (D7)",
        expand: "заходили через 7 дней и позже",
        meaning: "Доля пришедших, которые заходили через неделю после прихода или позже. Пришедшие меньше недели назад не считаются.",
        formula: "заходили через 7+ дней ÷ пришли не меньше 7 дней назад",
        example: "Из 60 пришедших неделю назад и раньше вернулись 18 — 30%.",
    },
    campD30: {
        label: "Вернулись через месяц (D30)",
        expand: "заходили через 30 дней и позже",
        meaning: "Доля пришедших, которые заходили через месяц после прихода или позже. Пришедшие меньше месяца назад не считаются.",
        formula: "заходили через 30+ дней ÷ пришли не меньше 30 дней назад",
        example: "Из 40 пришедших месяц назад и раньше вернулись 6 — 15%.",
    },

    // Роли
    campBuyerOnly: {
        label: "Только покупают",
        expand: "покупатели без продаж",
        meaning: "Пришедшие с покупками, но без продаж.",
        formula: "есть покупки и нет продаж",
        example: "Купил 2 скина и ничего не продавал.",
    },
    campSellerOnly: {
        label: "Только продают",
        expand: "продавцы без покупок",
        meaning: "Пришедшие с продажами, но без покупок. Комиссия со сделки учитывается у покупателя, поэтому их доход может быть небольшим.",
        formula: "есть продажи и нет покупок",
        example: "Продал скин за 500 ₽ и ничего не покупал.",
    },
    campHybrid: {
        label: "Покупают и продают",
        expand: "и то и другое",
        meaning: "Пришедшие, у которых есть и покупки, и продажи.",
        formula: "есть покупки и есть продажи",
        example: "Продал скин за 500 ₽ и на эти деньги купил другой за 450 ₽.",
    },
    campRoleCount: {
        label: "Человек",
        expand: "сколько людей в этой роли",
        meaning: "Число пришедших в этой роли.",
        formula: "число пришедших в роли",
        example: "Только покупают — 12 человек.",
    },
    campRoleShare: {
        label: "Доля",
        expand: "какая часть пришедших в этой роли",
        meaning: "Число людей в роли относительно всех пришедших. Те, у кого нет сделок, ни в одну роль не входят.",
        formula: "человек в роли ÷ пришли",
        example: "12 ÷ 100 = 12%.",
    },
    campRoleLtv: {
        label: "Доход на человека (LTV)",
        expand: "средний доход с человека этой роли",
        meaning: "Доход площадки от людей роли, делённый на их число.",
        formula: "доход роли ÷ человек в роли",
        example: "12 человек принесли 6 000 ₽: 6 000 ÷ 12 = 500 ₽.",
    },
    campRoleContribution: {
        label: "Доход роли",
        expand: "сколько заработали на этой роли",
        meaning: "Доход площадки от всех людей этой роли.",
        formula: "сумма дохода площадки по людям роли",
        example: "Комиссии 5 000 ₽ + с пополнений 1 000 ₽ = 6 000 ₽.",
    },
    campGmvBuy: {
        label: "Оборот покупок (GMV)",
        expand: "на какую сумму купили",
        meaning: "Сумма цен скинов, которые пришедшие купили (без возвратов).",
        formula: "сумма цен купленных скинов",
        example: "Купили 3 скина по 50 000 ₽ — 150 000 ₽.",
    },
    campGmvSell: {
        label: "Оборот продаж (GMV)",
        expand: "на какую сумму продали",
        meaning: "Сумма цен скинов, которые пришедшие продали.",
        formula: "сумма цен проданных скинов",
        example: "Продали 2 скина по 25 000 ₽ — 50 000 ₽.",
    },

    // Деньги
    campFirstDeposits: {
        label: "Первых депозитов",
        expand: "сколько человек пополнили баланс",
        meaning: "Число пришедших, которые после прихода и до конца кампании хотя бы раз пополнили баланс.",
        formula: "число людей с первым пополнением",
        example: "Пополнили 25 человек — 25 первых депозитов.",
    },
    campFirstDepositSum: {
        label: "Сумма первых",
        expand: "на сколько пополнили в первый раз",
        meaning: "Сумма первых пополнений пришедших, без последующих.",
        formula: "сумма первых пополнений",
        example: "25 человек впервые пополнили в среднем на 1 000 ₽ — 25 000 ₽.",
    },
    campFirstDepositAvg: {
        label: "Средний первый",
        expand: "средний размер первого пополнения",
        meaning: "Сумма первых пополнений, делённая на их число.",
        formula: "сумма первых ÷ первых депозитов",
        example: "25 000 ₽ ÷ 25 = 1 000 ₽.",
    },
    campDepositVolume: {
        label: "Объём депозитов",
        expand: "сколько всего пополнили",
        meaning: "Все пополнения пришедших за срок кампании — полная сумма, а не наш доход с них.",
        formula: "сумма всех пополнений",
        example: "Первые 25 000 ₽ + повторные 15 000 ₽ = 40 000 ₽.",
    },
    campWithdrawalVolume: {
        label: "Объём выводов",
        expand: "сколько всего вывели",
        meaning: "Все выводы пришедших за срок кампании — полная сумма.",
        formula: "сумма всех выводов",
        example: "Вывели три раза по 5 000 ₽ — 15 000 ₽.",
    },
    campNetFlow: {
        label: "Чистый приток",
        expand: "сколько пополнили сверх выведенного",
        meaning:
            "Пополнения минус выводы. Это не остаток на балансах: часть денег уже ушла продавцам. Больше 0 — пришедшие вносят больше, чем выводят.",
        formula: "объём депозитов − объём выводов",
        example: "40 000 − 15 000 = 25 000 ₽.",
    },
    campPayback: {
        label: "Окупаемость, дней",
        expand: "через сколько дней кампания отбилась",
        meaning:
            "Сколько дней прошло со дня создания кампании до дня, когда накопленный результат (доход площадки минус затраты на промо) впервые покрыл бюджет. День создания — нулевой. «—», если бюджет не указан или ещё не окупился.",
        formula: "дней от создания кампании до первого дня, когда накопленный результат ≥ бюджет",
        example: "Бюджет 10 000 ₽; через 12 дней после создания накопили 10 300 ₽ — окупаемость 12 дней.",
    },
    campCumulative: {
        label: "Накопленный результат",
        expand: "доход площадки нарастающим итогом",
        meaning: "Доход площадки минус затраты на промо, сложенный день за днём со дня создания кампании.",
        formula: "сумма (доход − затраты на промо) за все дни до текущего",
        example: "1-й день +500 ₽, 2-й +800 ₽ — на 2-й день накоплено 1 300 ₽.",
    },

    // Люди
    campOrders: {
        label: "Покупок",
        expand: "сколько раз человек купил",
        meaning: "Число завершённых покупок человека после прихода. Возвраты не считаются.",
        formula: "число завершённых заказов, где человек — покупатель",
        example: "Купил 3 раза, одна покупка ушла в возврат — покупок 2.",
    },
    campFee: {
        label: "Комиссия",
        expand: "сколько заработали на его покупках",
        meaning:
            "Комиссия площадки с покупок человека — по обычной ставке, без скидок по промокодам. Включает комиссию с возвратов: продавцу заплатили, хотя покупкой возврат не считается.",
        formula: "сумма комиссий с его покупок",
        example: "Две покупки, комиссия 50 ₽ и 70 ₽ — 120 ₽.",
    },
    campDepositProfit: {
        label: "С пополнений",
        expand: "наш доход с его пополнений",
        meaning: "Сколько площадка заработала на пополнениях человека (не сумма пополнений).",
        formula: "сумма нашего дохода с его пополнений",
        example: "Пополнил на 1 000 ₽, наш доход 2% — 20 ₽.",
    },
    campWithdrawalProfit: {
        label: "С выводов",
        expand: "наш доход с его выводов",
        meaning: "Сколько площадка заработала на выводах человека. Может быть отрицательным.",
        formula: "сумма нашего дохода с его выводов",
        example: "Вывел 2 000 ₽, наш доход 1% — 20 ₽.",
    },
    campPersonTotal: {
        label: "Итого",
        expand: "доход площадки с человека",
        meaning: "Комиссия + доход с пополнений + доход с выводов. Затраты на промо сюда не входят.",
        formula: "комиссия + с пополнений + с выводов",
        example: "120 + 20 + 20 = 160 ₽.",
    },
    campPersonPromo: {
        label: "Промо",
        expand: "во сколько обошлись его промокоды",
        meaning: "Затраты на бонусы по промокодам этого человека за срок кампании.",
        formula: "сумма его бонусов по промокодам",
        example: "Скидка 1% с покупки за 100 ₽ — минус 1 ₽.",
    },

    // Промокод
    campPromoActivations: {
        label: "Активаций",
        expand: "сколько раз ввели промокод",
        meaning: "Сколько раз промокод кампании активировали за её срок, в любом статусе.",
        formula: "число активаций промокода",
        example: "Промокод ввели 50 человек — 50 активаций.",
    },
    campPromoActive: {
        label: "Активны",
        expand: "ввели, но бонус ещё не сработал",
        meaning: "Активации, по которым ещё ждём первое событие с бонусом.",
        formula: "активации в статусе «активен»",
        example: "Ввели 50, у 12 бонус ещё не сработал — активны 12.",
    },
    campPromoRedeemed: {
        label: "Израсходованы",
        expand: "бонус сработал",
        meaning: "Активации, по которым бонус уже применился: скидка на покупку, бонус к пополнению или к продаже.",
        formula: "активации в статусе «израсходован»",
        example: "30 человек получили скидку или бонус — израсходовано 30.",
    },
    campPromoReplaced: {
        label: "Заменены",
        expand: "человек ввёл другой промокод",
        meaning: "Активации, которые человек заменил другим промокодом до того, как бонус сработал.",
        formula: "активации в статусе «заменён»",
        example: "5 человек ввели потом другой промокод — заменено 5.",
    },
    campPromoExpired: {
        label: "Истекли",
        expand: "кампания закончилась раньше, чем сработал бонус",
        meaning: "Активации, которые не успели сработать до конца кампании.",
        formula: "активации в статусе «истёк»",
        example: "3 человека так и не купили до конца кампании — истекло 3.",
    },
    campPromoConversion: {
        label: "Конверсия",
        expand: "у какой доли активаций сработал бонус",
        meaning: "Доля активаций, на которых бонус применился.",
        formula: "израсходованы ÷ активаций",
        example: "30 ÷ 50 = 60%.",
    },
    campPromoLimit: {
        label: "Лимит",
        expand: "сколько мест в лимите занято",
        meaning: "Активные и израсходованные активации за всё время к лимиту кампании. Заменённые и истёкшие места не занимают. «∞» — лимита нет.",
        formula: "(активны + израсходованы) / лимит",
        example: "12 + 30 = 42 из 100 — «42 / 100».",
    },
    campPromoLimitFill: {
        label: "Заполнение лимита",
        expand: "какая часть лимита занята",
        meaning: "Занятые места относительно лимита. «—», если лимита нет.",
        formula: "(активны + израсходованы) ÷ лимит",
        example: "42 ÷ 100 = 42%.",
    },
    campPromoTimeToRedeem: {
        label: "До применения",
        expand: "сколько ждали срабатывания бонуса",
        meaning: "Среднее время от ввода промокода до первого события с бонусом — по израсходованным активациям.",
        formula: "среднее (время применения − время активации)",
        example: "Ввёл промокод в 10:00, купил со скидкой в 14:00 — 4 часа.",
    },
    campBonusRedemptions: {
        label: "Применений",
        expand: "сколько раз сработал бонус этого типа",
        meaning: "Число применений бонуса этого типа за срок кампании.",
        formula: "число применений бонуса",
        example: "20 покупок со скидкой — 20 применений.",
    },
    campBonusTurnover: {
        label: "Оборот",
        expand: "сумма операций с бонусом",
        meaning: "Сумма заказов или пополнений, к которым применился бонус.",
        formula: "сумма операций с бонусом",
        example: "20 покупок по 100 ₽ — 2 000 ₽.",
    },
    campBonusCost: {
        label: "Затраты",
        expand: "во сколько обошёлся бонус",
        meaning: "Недобор комиссии с продавца, скидка покупателю или бонус к пополнению — сколько мы отдали.",
        formula: "сумма бонусов",
        example: "Скидка 1% с 2 000 ₽ — 20 ₽.",
    },
    campBonusAvgCost: {
        label: "В среднем",
        expand: "средние затраты на одно применение",
        meaning: "Затраты, делённые на число применений.",
        formula: "затраты ÷ применений",
        example: "20 ₽ ÷ 20 = 1 ₽.",
    },
    campPromoActivationsAndRedemptions: {
        label: "Активаций / применений",
        expand: "сколько раз ввели промокод и сколько раз сработал бонус",
        meaning: "Первое число — активации промокода кампании за её срок, второе — сколько из них израсходованы.",
        formula: "активаций / израсходованы",
        example: "Ввели 50, сработало у 30 — «50 / 30».",
    },
} as const satisfies Record<string, GlossaryTerm>;
